import { useContext, useState } from "react";
import { FollowSignalsContext } from "@shared/features/signals/common/FollowSignals.context.ts";
import {
  FollowSignalType,
  UseFollowSignalActionButton,
} from "@shared/features/signals/common/types.ts";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import { useGetSecondsTimeoutDisplay } from "@shared/features/bots/hooks.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useSelector } from "react-redux";
import { openMessage } from "utils/misc.tsx";

const useFollowSignalActionButton = ({
  signal,
  followType,
  onOpenModal,
}: UseFollowSignalActionButton) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const { openFollowModal } = useContext(FollowSignalsContext)!;
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const [noExchangeModalVisible, setNoExchangeModalVisible] = useState(false);
  const noActiveAccountsConnected =
    !!user && !user?.has_valid_clients_connected;
  const { brandingData } = useBranding();
  const { getSecondsTimeoutDisplay } = useGetSecondsTimeoutDisplay();

  const tradeExists = Boolean(
    signal.trade_exists?.length || signal.bot_trade_exists?.length
  );
  const requiresCancel = Boolean(
    signal.requires_cancel?.length || signal.bot_requires_cancel?.length
  );
  const requiresUpdate = Boolean(
    signal.requires_update?.length || signal.bot_requires_update?.length
  );
  const allowFollow = Boolean(
    signal.allow_follow?.length || signal.bot_allow_follow?.length
  );
  const followTimeout = brandingData?.follow_signal_timeout;

  const getFollowButtonError = () => {
    switch (followType) {
      case FollowSignalType.FOLLOW:
        if (!allowFollow) {
          if (tradeExists || requiresUpdate)
            return t("actions.errors.open_trade");
          else return t("actions.errors.no_account");
        }
        const isExpiredSignal =
          followTimeout &&
          signal.open_timestamp &&
          Date.now() / 1000 > signal.open_timestamp + followTimeout;
        if (isExpiredSignal) {
          const translatedTime = getSecondsTimeoutDisplay(followTimeout);
          return t("actions.errors.expired_signal", { time: translatedTime });
        }
        break;
      case FollowSignalType.UPDATE:
        if (!requiresUpdate) {
          if (tradeExists) return t("actions.errors.open_trade_updated");
          else return t("actions.errors.no_open_trade");
        }
        break;
      case FollowSignalType.CANCEL:
        return !requiresCancel && t("actions.errors.no_open_trade");
    }
  };

  const showNoExchangeError =
    followType === FollowSignalType.FOLLOW && noActiveAccountsConnected;
  const errorMessage = getFollowButtonError();

  const openFollowSignalModal = () => {
    openFollowModal(signal, followType);
    onOpenModal?.();
  };

  const onClick = () => {
    if (showNoExchangeError) {
      setNoExchangeModalVisible(true);
    } else if (errorMessage) {
      openMessage(errorMessage, "warning");
    } else {
      openFollowSignalModal();
    }
  };

  return {
    text: t(BUTTON_TEXT_MAPPING[followType]),
    hoverMessage: t(BUTTON_HOVER_MESSAGE_MAPPING[followType]),
    onClick,
    disabled: !!errorMessage || showNoExchangeError,
    noExchangeModalVisible,
    setNoExchangeModalVisible,
    showNoExchangeError,
  };
};

export default useFollowSignalActionButton;

const BUTTON_TEXT_MAPPING: Record<
  FollowSignalType,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  [FollowSignalType.FOLLOW]: "actions.follow",
  [FollowSignalType.UPDATE]: "actions.update",
  [FollowSignalType.CANCEL]: "actions.close",
};
const BUTTON_HOVER_MESSAGE_MAPPING: Record<
  FollowSignalType,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  [FollowSignalType.FOLLOW]: "actions.follow_signal",
  [FollowSignalType.UPDATE]: "actions.update_trade",
  [FollowSignalType.CANCEL]: "actions.close_trade",
};
